import { useEffect } from 'react';

import { FORM_DATA_KEY_FACTORY, useFormDataQuery } from '@/api/getters';
import { useSubmitStagesMutation } from '@/api/setters';
import { BUSINESS_TYPE, Stages } from '@/api/types';
import Chat from '@/assets/chat.svg?react';
import Flower from '@/assets/flower.svg?react';
import BlockOverlayContent, {
  BlockOverlayContentProps,
} from '@/components/layouts/BlockOverlayContent';
import Modal, {
  ModalContent,
  ModalDescription,
  ModalTitle,
} from '@/components/ui/Modal';
import { useStepper } from '@/features/v2/lib/stepper';
import cn from '@/utils/cn';

import { useLingui } from '@lingui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

interface ConfirmationModalProps {
  handleClose?: () => void;
  withoutCloseButton?: boolean;
  classNameModal?: string;
  content: BlockOverlayContentProps;
}

const ConfirmationModal = ({
  handleClose,
  withoutCloseButton,
  classNameModal,
  content,
}: ConfirmationModalProps) => {
  const canDismiss = !withoutCloseButton && !!handleClose;

  const preventNonDismissableClose = (event: Event) => {
    if (!canDismiss) event.preventDefault();
  };

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open && canDismiss) handleClose?.();
      }}
    >
      <ModalContent
        className={cn(
          'max-w-[310px] gap-6 rounded-[28px] p-6 shadow-[0px_6px_16px_0px_rgba(0,0,0,0.16)] lg:max-w-[360px] lg:p-8',
          classNameModal
        )}
        onEscapeKeyDown={preventNonDismissableClose}
        onPointerDownOutside={preventNonDismissableClose}
      >
        <BlockOverlayContent
          {...content}
          onClose={withoutCloseButton ? undefined : handleClose}
          titleAs={ModalTitle}
          descriptionAs={ModalDescription}
        />
      </ModalContent>
    </Modal>
  );
};

const SummaryModal = ({
  open,
  toggle,
}: {
  open: boolean;
  toggle: () => void;
}) => {
  const { i18n } = useLingui();
  const { data } = useFormDataQuery();
  const submitStages = useSubmitStagesMutation();
  const queryClient = useQueryClient();
  const { step, setStep } = useStepper();

  const { id: hash } = useParams();

  useEffect(() => {
    if (data?.stage === Stages.STAGE_IV_FINISHED) {
      let refreshCount = parseInt(
        localStorage.getItem('refreshCount') || '0',
        10
      );

      const maxRefreshes = 40;
      const interval = setInterval(() => {
        if (refreshCount >= maxRefreshes) {
          clearInterval(interval);
        } else {
          queryClient.invalidateQueries({
            queryKey: FORM_DATA_KEY_FACTORY(hash),
          });
          refreshCount++;

          localStorage.setItem('refreshCount', refreshCount.toString());
        }
      }, 1000 * 6); // every 6 seconds

      return () => clearInterval(interval);
    }

    if (data?.stage === Stages.STAGE_V && step !== 7) {
      setStep(7);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.stage]);

  const isCompany =
    data?.offer.business_type &&
    data?.offer.business_type !== BUSINESS_TYPE.JDG;

  if (!data) return;

  return (
    <>
      {open && data?.stage === Stages.STAGE_IV && (
        <ConfirmationModal
          handleClose={toggle}
          withoutCloseButton={submitStages.isPending}
          classNameModal="w-full"
          content={{
            info: isCompany
              ? i18n.t(
                  'Potwierdź, że wszyscy reprezentanci są uwzględnieni w formularzu.'
                )
              : i18n.t(
                  'Potwierdź, że wszyscy współwłaściciele pojazdu są uwzględnieni w formularzu.'
                ),
            title: isCompany
              ? i18n.t('Dodano każdego reprezentanta?')
              : i18n.t('Dodano każdego współwłaściciela?'),
            secondaryButtonContent: i18n.t('Popraw dane'),
            buttonFlexDirection: 'flex-col',
            icon: Chat,
            loading: submitStages.isPending,
            buttonConfirm: {
              content: i18n.t('Tak, generuj dokumenty'),
              onClick: async () => {
                localStorage.removeItem('refreshCount');
                await submitStages.mutateAsync(data.stage);
              },
            },
          }}
        />
      )}
      {open && data?.stage === Stages.STAGE_IV_FINISHED && (
        <ConfirmationModal
          withoutCloseButton
          classNameModal="w-full"
          content={{
            title: i18n.t('Przygotowywanie dokumentów'),
            info: i18n.t(
              'Twoje dokumenty są w trakcie generowania i już niedługo będą gotowe. <b class="font-semibold">Nie zamykaj tego okna,</b><br> może to zająć około minuty.'
            ),
            loadingIcon: true,
            disabled: true,
            buttonConfirm: {
              content: i18n.t('Podpisz'),
            },
          }}
        />
      )}
      {open && data?.stage === Stages.STAGE_V && (
        <ConfirmationModal
          withoutCloseButton
          classNameModal="w-full"
          content={{
            title: i18n.t('Gotowe'),
            icon: Flower,
            buttonConfirm: {
              content: i18n.t('Zamknij'),
              onClick: () => {
                toggle();
                setTimeout(() => {
                  window.scrollTo(0, 0);
                }, 200);
              },
            },
          }}
        />
      )}
    </>
  );
};

export default SummaryModal;
